/**
 * Виджет «Крупнейшие проекты» (US-26 FR-7).
 *
 * Перечень из пяти проектов по общей стоимости по убыванию: номер,
 * название, второй строкой территория и АБП, справа общая стоимость и
 * процент «освоено всего» (FR-7.1). Клик по проекту открывает **ту же**
 * карточку, что и строка реестра, — по `project_uid` (FR-7.2).
 *
 * Охват — общий охват раздела (FR-7.3); перечень приходит из выдачи
 * готовым (`/public/projects/widgets/largest`), экран не пересобирает его
 * из строк реестра: реестр отфильтрован и постраничен, а виджет считается
 * по всей территории.
 */
import { useTranslation } from 'react-i18next';

import { moneyMln, pctInt } from './format';
import { NoData, useLocalizedText } from './projectsView';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import type { ProjectsLargestRow } from '../../../types/projects';

interface LargestProjectsWidgetProps {
  rows: ProjectsLargestRow[];
  onOpen: (projectUid: string) => void;
}

export const LargestProjectsWidget = ({
  rows,
  onOpen,
}: LargestProjectsWidgetProps) => {
  const { t } = useTranslation();
  const loc = useLocalizedText();

  return (
    <Card className="h-full shadow-card" data-testid="block-projects-largest">
      <CardHeader className="flex-row flex-wrap items-baseline gap-x-3 gap-y-1 space-y-0">
        <CardTitle>{t('projects.largest.title')}</CardTitle>
        <span className="text-xs text-text-faint">
          {t('projects.largest.hint')}
        </span>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p
            className="py-8 text-center text-sm lowercase text-text-muted"
            data-testid="block-placeholder"
          >
            {t('common.noData')}
          </p>
        ) : (
          <ul className="divide-y divide-surface-border">
            {rows.map((row, index) => (
              <li key={row.projectUid}>
                <button
                  type="button"
                  onClick={() => onOpen(row.projectUid)}
                  className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 rounded-lg px-1 py-2.5 text-left hover:bg-brand-50/60 focus-visible:bg-brand-50/60"
                  data-testid={`projects-largest-row-${row.projectUid}`}
                >
                  <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-surface-muted text-xs font-bold text-text-muted">
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold leading-snug">
                      {loc(row.name)}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-text-muted">
                      {[loc(row.katoName), loc(row.abpName)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="block whitespace-nowrap text-sm font-bold tabular-nums">
                      {row.totalCost === null ? (
                        <NoData />
                      ) : (
                        `${moneyMln(row.totalCost)} ${t('projects.units.mlnTg')}`
                      )}
                    </span>
                    <span className="block text-xs text-text-muted">
                      {t('projects.largest.totalCost')}
                    </span>
                  </span>
                  <span className="w-16 shrink-0 text-right">
                    <span className="block text-sm font-bold tabular-nums text-brand-700">
                      {row.executionPct === null ? (
                        <NoData />
                      ) : (
                        pctInt(row.executionPct)
                      )}
                    </span>
                    <span className="block text-xs text-text-muted">
                      {t('projects.largest.spent')}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
